import { auth } from "auth"
import { useToggle } from "hooks"
import { Dropdown } from "materia"
import { useState, type ComponentProps } from "react"
import { useTranslation } from "react-i18next"
import { ATTENSI_PRODUCT_PLATFORM_FOR_OS } from "../../../constants"
import { getLocaleDisplayName } from "../../../dataUtilities/getLocaleDisplayName"
import { createPlaythrough } from "../../../graphql/createPlaythrough"
import { addPlayLinkParams } from "../../../tools/addPlayLinkParams"
import { APP_TYPE, IS_APP } from "../../../vuplex/constants"
import { isHandledByVuplex } from "../../../vuplex/isHandledByVuplex"
import { sendOpenProductMessage } from "../../../vuplex/sendOpenProductMessage"
import { sendVuplexMessage } from "../../../vuplex/sendVuplexMessage"
import { HowToPlay } from "../ProductModalHelp"
import { HowToDownloadText } from "../ProductModalHelp/HowToDownloadText"
import { ProductModalMain } from "../ProductModalMain"
import { LauncherOverlay, NotSupported } from "../components"
import { RealTalkProductButtons } from "./RealTalkProductButtons"
import type { Data } from "./gql"

import s from "./ProductModalButtons.module.scss"

export const ProductModalButtons = ({ data }: { data: Data }) => {
  const { productType, playLinks, useTransferToken, supportedPlatforms } = data

  switch (productType) {
    case "skill":
    case "behavior":
    case "operations":
    case "process":
    case "workflow":
    case "act":
      return <PlayableProductButtons data={data} />
    case "app":
      return <AppProductButtons data={data} />
    case "fast":
    case "assessment":
    case "process_on_web":
    case "fast_survey":
    case "dialogue":
    case "quiz_race":
    case "skills_real_talk":
      return <WebGamesProductButton data={data} />
    case "file":
      return <FileProductButton data={data} />
    case "link":
      return <LinkProductButton data={data} />
    case "pdf":
      return <PdfProductButton data={data} />
    case "real_talk":
    case "real_talk_maker":
      return (
        <RealTalkProductButtons
          productType={productType}
          playLinks={playLinks}
          useTransferToken={useTransferToken}
          supportedPlatforms={supportedPlatforms}
        />
      )
    case "video":
      return <VideoProductButton data={data} />
    case "xapi":
      return <XapiProductButton data={data} />
    case "scorm":
      return <ScormProductButton data={data} />
  }
}

const XapiProductButton = ({ data }: { data: Data }) => {
  const { t } = useTranslation()

  const { contentLink, scormStrictScoring, openExternalBrowser } = data

  const openInModal = scormStrictScoring && !openExternalBrowser

  const linkProps: Partial<PlayLinkProps> = openInModal
    ? { to: "play" }
    : {
        target: "_blank",
        getLink: () => auth.addAccessParams(contentLink),
      }

  return (
    <PlayLink data={data} {...linkProps}>
      {t("product.PLAY_TEXT_SIM")}
    </PlayLink>
  )
}

const ScormProductButton = ({ data }: { data: Data }) => {
  const { t } = useTranslation()

  return (
    <PlayLink data={data} to="play">
      {t("product.PLAY_TEXT_SIM")}
    </PlayLink>
  )
}

// Video playthrough is created in the ViewerModal.
// That way we can defer it for unskippable videos.
const VideoProductButton = ({ data }: { data: Data }) => {
  const { t } = useTranslation()
  const { localeContentLinks } = data
  const links = localeContentLinks.nodes

  const { selectedLink, dropdown } = useLocalizedLinkDropdown(links)
  const locale = selectedLink?.locale

  return (
    <>
      {dropdown}
      <PlayLink
        data={data}
        to="play"
        search={(search) => ({ ...search, content_locale: locale })}
      >
        {t("product.PLAY_TEXT_VIDEO")}
      </PlayLink>
    </>
  )
}

const WebGamesProductButton = ({ data }: { data: Data }) => {
  const { t } = useTranslation()
  const { contentLink, openExternalBrowser, productType } = data

  const openExternalLink = async () => {
    const link = await auth.addAccessParams(contentLink)

    sendVuplexMessage({
      type: "OPEN_LINK",
      payload: { link, openExternalBrowser: true },
    })
  }

  if (IS_APP && openExternalBrowser) {
    return (
      <ProductModalMain.PlayLink onClick={openExternalLink}>
        {t("product.PLAY_TEXT_SIM")}
      </ProductModalMain.PlayLink>
    )
  }

  const onClick = () => {
    if (productType === "quiz_race") {
      createPlaythrough(data.id)
    }
  }

  return (
    <PlayLink data={data} to="play" onClick={onClick}>
      {t("product.PLAY_TEXT_SIM")}
    </PlayLink>
  )
}

// PDF playthrough is created upon successful PDF load.
const PdfProductButton = ({ data }: { data: Data }) => {
  const { t } = useTranslation()
  const {
    productUploads: { nodes: uploads },
  } = data

  const { selectedLink, dropdown } = useLocalizedLinkDropdown(uploads)
  const locale = selectedLink?.locale

  return (
    <>
      {dropdown}
      <PlayLink
        data={data}
        to="play"
        search={(search) => ({ ...search, content_locale: locale })}
      >
        {t("product.PLAY_TEXT_PDF")}
      </PlayLink>
    </>
  )
}

const FileProductButton = ({ data }: { data: Data }) => {
  const { t } = useTranslation()
  const { id, contentLink, productUploads } = data
  const uploads = productUploads.nodes

  const { selectedLink, dropdown } = useLocalizedLinkDropdown(uploads)

  return (
    <>
      {dropdown}
      <PlayLink
        data={data}
        href={selectedLink?.url ?? contentLink}
        iconVariant="Download"
        target="_blank"
        onClick={() => createPlaythrough(id)}
      >
        {t("product.DOWNLOAD_TEXT_SIM")}
      </PlayLink>
    </>
  )
}

const LinkProductButton = ({ data }: { data: Data }) => {
  const { t } = useTranslation()

  const { localeContentLinks, contentLink } = data
  const links = localeContentLinks.nodes
  const { selectedLink, dropdown } = useLocalizedLinkDropdown(links)

  const link = selectedLink?.link ?? contentLink

  // This is specifically for linking to Discourse forums
  // https://jira.attensi.com/browse/WEB-15665
  const isDiscourseLink =
    link === "https://api.attensi.com/api/public/discourse/callback"

  const getDiscourseLink = () => {
    return auth.addAccessParams(link)
  }

  return (
    <>
      {dropdown}
      <PlayLink
        data={data}
        target="_blank"
        href={isDiscourseLink ? undefined : link}
        getLink={isDiscourseLink ? getDiscourseLink : undefined}
        onClick={() => createPlaythrough(data.id)}
      >
        {t("product.PLAY_TEXT_LINK")}
      </PlayLink>
    </>
  )
}

const PlayableProductButtons = ({ data }: { data: Data }) => {
  const { t } = useTranslation()

  const [isLauncherOverlayVisible, toggleLauncherOverlay] = useToggle()
  const { playLinks, hasVppCodes, useTransferToken, supportedPlatforms } = data
  const { appLink, appStoreLink, launcherLink, webglLink } = playLinks

  const isPlayable = webglLink || appLink || launcherLink

  if (!isPlayable) {
    return <NotSupported supportedPlatforms={supportedPlatforms} />
  }

  const getLink = (link: string | null) => {
    return addPlayLinkParams({ link, useTransferToken })
  }

  // The way apps are downloaded depend on the OS and store type:
  // 1. For Android, the Play Store link is used (appLink)
  // 2. For iOS, there are two types of stores where app might be available:
  //   a. public app store - the traditional Apple Store link can be used (appStoreLink)
  //   b. business app store - long story short, you need a company issued redeemable code
  //      to be able to download apps from there. They are called VPP codes.
  // Depending on the download strategy, we show different buttons for playable products.
  const isAppDownloadableWithVPP =
    hasVppCodes && ATTENSI_PRODUCT_PLATFORM_FOR_OS === "iOS"
  const isAppDownloadableWithLink =
    !isAppDownloadableWithVPP && APP_TYPE !== "skills"
  const isDownloadHelpTextVisible = Boolean(
    isAppDownloadableWithLink && appStoreLink && appLink
  )
  const isMultiplePlayLinks = launcherLink && webglLink
  const webglLinkProps: Partial<PlayLinkProps> = data.webglResponsive
    ? { to: "play" }
    : {
        target: "_blank",
        getLink: () => getLink(webglLink),
      }

  return (
    <div className={s.playableProductButtons}>
      <div className={s.playableProductButtons__actions}>
        {isAppDownloadableWithVPP && (
          <PlayLink data={data} iconVariant="Download" to="vpp">
            {t("product.DOWNLOAD_TEXT_SIM")}
          </PlayLink>
        )}

        {isAppDownloadableWithLink && appStoreLink && (
          <PlayLink
            data={data}
            href={appStoreLink}
            target="_blank"
            iconVariant="Download"
          >
            {t("product.DOWNLOAD_TEXT_SIM")}
          </PlayLink>
        )}

        {appLink && (
          <PlayLink data={data} getLink={() => getLink(appLink)}>
            {t("product.PLAY_TEXT_SIM")}
          </PlayLink>
        )}

        {webglLink && (
          <PlayLink data={data} {...webglLinkProps}>
            {isMultiplePlayLinks
              ? t("content.PRODUCT_WEBGL_LAUNCHER")
              : t("product.PLAY_TEXT_SIM")}
          </PlayLink>
        )}

        {launcherLink && (
          <PlayLink
            data={data}
            onClick={toggleLauncherOverlay}
            linkVariant={isMultiplePlayLinks ? "ghost" : "theme"}
            getLink={() => getLink(launcherLink)}
          >
            {isMultiplePlayLinks
              ? t("content.PRODUCT_DESKTOP_LAUNCHER")
              : t("product.PLAY_TEXT_SIM")}
          </PlayLink>
        )}

        {isMultiplePlayLinks && <HowToPlay playType={["webgl", "launcher"]} />}

        {isLauncherOverlayVisible && (
          <LauncherOverlay onBack={toggleLauncherOverlay} />
        )}
      </div>
      <HowToDownloadText isVisible={isDownloadHelpTextVisible} />
    </div>
  )
}

const AppProductButtons = ({ data }: { data: Data }) => {
  const { t } = useTranslation()
  const { id, contentLink, playLinks, supportedPlatforms } = data

  const { appStoreLink } = playLinks

  if (!appStoreLink) {
    return <NotSupported supportedPlatforms={supportedPlatforms} />
  }

  const vuplexCanHandleDownload = IS_APP && Boolean(contentLink)
  const showDownloadLink = appStoreLink && !vuplexCanHandleDownload

  return (
    <>
      {showDownloadLink && (
        <PlayLink
          data={data}
          href={appStoreLink}
          target="_blank"
          iconVariant="Download"
          onClick={() => createPlaythrough(id)}
        >
          {t("product.DOWNLOAD_TEXT_SIM")}
        </PlayLink>
      )}

      {contentLink && (
        <PlayLink
          data={data}
          href={contentLink}
          target="_blank"
          onClick={() => createPlaythrough(id)}
        >
          {t("product.PLAY_TEXT_SIM")}
        </PlayLink>
      )}
    </>
  )
}

type PlayLinkProps = ComponentProps<typeof ProductModalMain.PlayLink> & {
  data: Data
  getLink?: () => Promise<string | undefined>
}
const PlayLink = ({
  data,
  getLink,
  onClick,
  href,
  ...props
}: PlayLinkProps) => {
  const { productType } = data

  const shouldDelegateToVuplex = IS_APP && isHandledByVuplex(productType)

  const handleClick = async () => {
    onClick?.()

    const dynamicLink = await getLink?.()

    if (shouldDelegateToVuplex) {
      if (dynamicLink) {
        sendOpenProductMessage(data, dynamicLink)
      } else if (href) {
        sendOpenProductMessage(data, href)
      }
    } else if (dynamicLink) {
      if (props.target === "_blank") {
        window.open(dynamicLink)
      } else {
        window.location.href = dynamicLink
      }
    }
  }

  return (
    <ProductModalMain.PlayLink
      {...props}
      onClick={handleClick}
      href={shouldDelegateToVuplex ? undefined : href}
    />
  )
}

const useLocalizedLinkDropdown = <T extends { locale: string }>(
  localeLinks: T[]
) => {
  const { i18n } = useTranslation()

  const preferredLink = localeLinks.find(
    (localeLink) => localeLink.locale === i18n.language
  )
  const [selectedLink, setSelectedLink] = useState(
    preferredLink ?? localeLinks[0]
  )

  const options = localeLinks.map((localeLink) => (
    <Dropdown.Option key={localeLink.locale} value={localeLink}>
      {getLocaleDisplayName(localeLink.locale)}
    </Dropdown.Option>
  ))

  const dropdown = selectedLink && !preferredLink && localeLinks.length > 1 && (
    <ProductModalMain.LanguageDropdown>
      <Dropdown value={selectedLink} onChange={setSelectedLink}>
        <Dropdown.Button>
          {getLocaleDisplayName(selectedLink.locale)}
        </Dropdown.Button>
        <Dropdown.Options>{options}</Dropdown.Options>
      </Dropdown>
    </ProductModalMain.LanguageDropdown>
  )

  return { selectedLink, dropdown }
}
